import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { emissionValueWithDecimals } from '../../../../../../../../../../../shared/helpers/emission-value-with-decimals';
import {
  DeclaredUnit,
  DeclaredUnitToPlainSymbolsMap,
} from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../shared/interfaces/IFootprint';
import { IInputMaterial } from '../../../PcfWizard.type';

type MaterialDetailsProps = {
  inputMaterial: IInputMaterial;
};

const MaterialDetails = ({ inputMaterial }: MaterialDetailsProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-column gap-3 w-full'>
      <span className='text-lg font-medium'>
        {t('calculationDetailsPage.inputMaterial.material')}
      </span>

      <div className='grid w-full grid-nogutter'>
        {/* Primary Material */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.inputMaterial.primaryMaterial')}
          </span>
          <span className='text-lg'>{inputMaterial.material_name}</span>
        </div>

        {/* Amount per Piece */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.inputMaterial.amountPerPiece')}
          </span>

          <span className='text-lg'>
            {emissionValueWithDecimals(inputMaterial.amount_per_piece ?? undefined)}{' '}
            {inputMaterial?.declared_unit &&
              DeclaredUnitToPlainSymbolsMap[inputMaterial.declared_unit as DeclaredUnit]}
          </span>
        </div>
      </div>

      {/* Emission Factor */}
      <div className='flex flex-column gap-1'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.inputMaterial.emissionFactor')}
        </span>
        <div className='flex flex-row gap-1 text-lg'>
          <EmissionValueWithSuffix
            emissionValue={inputMaterial.pcf_excluding_biogenic}
            declaredUnit={inputMaterial.declared_unit}
            unitaryProductAmount={inputMaterial.unitary_product_amount}
            emissionSymbol={EmissionSymbol.KG_CO2E}
            abbreviateUnit={true}
            emissionValueDecimals={6}
            numericalValueTextClassName='text-lg'
            suffixTextClassName='text-lg'
          />
        </div>
      </div>

      {/* Emissions */}
      <div className='flex flex-column gap-1'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.inputMaterial.emissions')}
        </span>

        <EmissionValueWithSuffix
          emissionValue={inputMaterial.emissions}
          declaredUnit={DeclaredUnit.piece}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      </div>
    </div>
  );
};

export default MaterialDetails;
